import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import {
  Stack,
  TextField,
  Button,
  Modal,
  Checkbox,
  FormGroup,
  FormControlLabel,
  IconButton,
  Divider,
  Typography,
  Tooltip
} from '@mui/material';

import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InfoIcon from '@mui/icons-material/Info';
import { cores } from '../styles/styleUtils';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60vw', // Aumentei um pouco para caber melhor as duas colunas
  height: '80vh',
  '@media screen and (max-width: 900px)': { // Ajuste de media query para telas médias
    width: '90vw',
  },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`,
  borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 3,
  display: 'flex',
  flexDirection: 'column', // Mudado para column para gerenciar melhor o header/conteudo/footer
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
};

const styleTextField = {
  backgroundColor: 'white',
  width: '100%'
};

// Componente auxiliar para o Tooltip das variáveis
const InfoTooltip = () => (
    <Tooltip title={
      <Stack sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Variáveis disponíveis:</Typography>
        <Typography variant="caption">{'{nomecliente}'} - Nome do cliente</Typography>
        <Typography variant="caption">{'{nomeusuario}'} - Nome do Usuário</Typography>
        <Typography variant="caption">{'{codigo}'} - Código da OS (TTTAAAAMMDD)</Typography>
      </Stack>
    }>
      <IconButton size="small" color="primary">
        <InfoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

// ----------------------------------------------------------------------

const AddMensagem = React.forwardRef((props, ref) => {
  const [data, setData] = useState(props.data || {}); // Garante que data não seja undefined inicialmente

  useEffect(() => {
    setData(props.data || {});
  }, [props.data]);


  const handleChange = (e) => {
    const newValue = e.target.value;
    const property = e.target.id;
    setData({ ...data, [property]: newValue });
  }

  const handleChangeDestino = (e, index) => {
    const newArray = [...(data.destino || [])];
    newArray[index] = e.target.value;
    setData({ ...data, destino: newArray });
  }

  const handleChangeCheck = (e) => {
    setData({ ...data, [e.target.id]: e.target.checked });
  };

  const handleConfirm = () => {
    props.onConfirm(data);
  };

  const onClose = () => {
    props.onClose();
  };

  const addDestino = () => {
    const newArray = data.destino ? [...data.destino] : [];
    newArray.push('');
    setData({ ...data, destino: newArray });
  }

  const removeDestino = (e, index) => {
    const newArray = [...data.destino];
    newArray.splice(index, 1);
    setData({ ...data, destino: newArray });
  }


  // ----------------------
  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack sx={style} spacing={2}>
        
        {/* Área de Conteúdo Principal dividida em 2 colunas */}
        <Stack 
            direction={{ xs: 'column', md: 'row' }} // Em telas pequenas fica um embaixo do outro, em telas medias+ fica lado a lado
            spacing={3} 
            divider={<Divider orientation="vertical" flexItem />}
            sx={{ flex: 1, overflowY: 'auto', mb: 6 }} // mb: 6 para dar espaço para os botões fixos
        >

            {/* --- COLUNA DA ESQUERDA (Interna/Destinos) --- */}
            <Stack spacing={2} sx={{ flex: 1 }}>
                <Typography variant="h6" textAlign="center" sx={{ color: cores.textoClaro }}>Interno</Typography>
                
                <FormControlLabel 
                    sx={{ color: cores.textoClaro }}
                    control={<Checkbox id='enviamensagem' onChange={handleChangeCheck} checked={data.enviamensagem || false} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: cores.laranja } }} />} 
                    label="Envia Mensagem Interna" 
                />
                
                {data.enviamensagem && (
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" sx={{ color: cores.textoClaro }}>Mensagem Interna</Typography>
                            <InfoTooltip />
                        </Stack>
                         <TextField
                            multiline
                            rows={4}
                            id="mensagem"
                            label="Texto da Mensagem"
                            variant="outlined"
                            value={data.mensagem || ''}
                            onChange={handleChange}
                            sx={{ backgroundColor: 'white' }}
                            fullWidth
                        />

                        <Typography variant="subtitle2" sx={{ mt: 2, color: cores.textoClaro }}>Destinos</Typography>
                        {data.destino?.map((dest, index) => (
                            <Stack key={index} direction={'row'} spacing={1} sx={{ width: '100%' }}>
                                <TextField 
                                    label={`Destino ${index + 1}`} 
                                    variant="outlined" 
                                    value={dest} 
                                    onChange={(e) => handleChangeDestino(e, index)} 
                                    sx={styleTextField} 
                                    size="small"
                                />
                                <IconButton aria-label="remove" color="error" onClick={(e) => removeDestino(e, index)}>
                                    <RemoveCircleIcon />
                                </IconButton>
                            </Stack>
                        ))}
                        <Button
                            variant="outlined"
                            onClick={addDestino}
                            startIcon={<AddCircleIconPlaceholder />}
                            sx={{ color: cores.laranja, borderColor: cores.laranja }}
                        >
                             Adicionar Destino
                        </Button>
                    </Stack>
                )}
            </Stack>

             {/* --- COLUNA DA DIREITA (Cliente) --- */}
            <Stack spacing={2} sx={{ flex: 1 }}>
                <Typography variant="h6" textAlign="center" sx={{ color: cores.textoClaro }}>Cliente</Typography>

                <FormControlLabel 
                    sx={{ color: cores.textoClaro }}
                    control={<Checkbox id='enviaMensagemCliente' onChange={handleChangeCheck} checked={data.enviaMensagemCliente || false} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: cores.laranja } }} />} 
                    label="Enviar Mensagem Cliente" 
                />

                {data.enviaMensagemCliente && (
                    <Stack spacing={2}>
                         <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" sx={{ color: cores.textoClaro }}>Mensagem para o Cliente</Typography>
                            <InfoTooltip />
                        </Stack>
                        <TextField
                            multiline
                            rows={6} // Um pouco maior pois não tem destinos embaixo
                            id="mensagemCliente"
                            label="Texto da Mensagem Cliente"
                            variant="outlined"
                            value={data.mensagemCliente || ''}
                            onChange={handleChange}
                            sx={{ backgroundColor: 'white' }}
                            fullWidth
                        />
                    </Stack>
                )}
            </Stack>

        </Stack>

        {/* Área dos Botões (Fixa no rodapé do modal) */}
        <Stack direction={'row'} justifyContent={'space-between'} sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`, borderTop: `1px solid ${cores.chumbo}` }}>
          <Button variant="contained" onClick={onClose} color="error"> Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleConfirm}>Salvar</Button>
        </Stack>

      </Stack>
    </Modal>
  );
})

// Ícone placeholder caso você não tenha importado o AddCircle
const AddCircleIconPlaceholder = () => <span>+</span>;

AddMensagem.propTypes = {
  data: PropTypes.object, // Corrigi de 'produto' para 'data' baseado no uso interno
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onClose: PropTypes.func,
}

export default (AddMensagem);